/**
 * Report-sourced alert detail (validation threshold breach, OCF reconciliation
 * mismatch). These `alert_fired` payloads carry flat numeric metric fields
 * rather than the nested `metrics` object used by insights, so we surface the
 * friendly numeric fields as a KPI grid and deep-link to the agent's bookings.
 */
import type { ReportAlertPayload } from "@/types/feed";

import { agentBookingsHref } from "../../feedLinks";
import {
  formatMetricValue,
  isFriendlyMetricKey,
  pickMetricLabel,
} from "../formatInsight";
import { CtaLink, InsightShell, MetricGrid, type MetricItem } from "../primitives";

const SOURCE_LABEL: Record<string, string> = {
  validation_threshold: "Data validation",
  ocf_reconciliation_report: "OCF reconciliation",
};

export function ReportAlertView({ payload }: { payload: ReportAlertPayload }) {
  const metricItems: MetricItem[] = Object.entries(payload)
    .filter(
      ([key, value]) => typeof value === "number" && isFriendlyMetricKey(key),
    )
    .map(([key, value]) => ({
      label: pickMetricLabel(key),
      value: formatMetricValue(key, value as number),
    }));

  const agent = typeof payload.agent === "string" ? payload.agent : undefined;
  const explainer =
    SOURCE_LABEL[payload.source] ?? undefined;

  return (
    <InsightShell explainer={explainer}>
      {metricItems.length > 0 && <MetricGrid items={metricItems} />}
      {agent && (
        <CtaLink to={agentBookingsHref(agent)}>View agent bookings</CtaLink>
      )}
    </InsightShell>
  );
}
